/**
 * The "New navigation" switch: the pillar sidebar (platform-restructure-spec v2,
 * Phase 1) instead of the classic Agent Atlas section.
 *
 * Two gates, both needed:
 *   - the PILLAR_NAV flag decides who is offered the switch (the development
 *     organisation while it is built);
 *   - the person's own choice, kept per browser, decides whether it is on.
 * Off by default, so nobody's sidebar changes until they flip it.
 *
 * Sidebar and the account menu read the same store, so flipping the switch
 * redraws the sidebar at once (and other tabs follow via the storage event).
 */
import { useSyncExternalStore } from 'react';
import { isFeatureEnabled } from '../../../utils/featureFlags';

export const PILLAR_NAV_STORAGE_KEY = 'saf3ai.nav.pillars';

const listeners = new Set();
const notify = () => listeners.forEach((l) => l());

const readChoice = () => {
  try {
    return localStorage.getItem(PILLAR_NAV_STORAGE_KEY) === 'on';
  } catch {
    return false;
  }
};

/** Whether this user may use the switch at all. */
export const isPillarNavAvailable = () => isFeatureEnabled('PILLAR_NAV');

/** Whether the pillar sidebar is drawn: offered AND switched on. */
export const isPillarNavOn = () => isPillarNavAvailable() && readChoice();

export const setPillarNav = (on) => {
  try {
    localStorage.setItem(PILLAR_NAV_STORAGE_KEY, on ? 'on' : 'off');
  } catch {
    // Storage blocked (private window): the choice lasts until reload.
  }
  notify();
};

const subscribe = (listener) => {
  listeners.add(listener);
  // Another tab flipped the switch, or a different user signed in.
  const onStorage = (e) => {
    if (e.key === PILLAR_NAV_STORAGE_KEY || e.key === 'user') listener();
  };
  window.addEventListener('storage', onStorage);
  return () => {
    listeners.delete(listener);
    window.removeEventListener('storage', onStorage);
  };
};

export default function usePillarNav() {
  const on = useSyncExternalStore(subscribe, isPillarNavOn, () => false);
  return { available: isPillarNavAvailable(), on, setOn: setPillarNav };
}
